import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';
import { questionApi } from '../../api/questionApi';
import { questionSetApi } from '../../api/questionSetApi';
import { authApi } from '../../api/auth';
import type { Question } from '../../types';
import { QuestionEditForm } from '@shared-ui/question-edit-form';
import type { QuestionFormData, ConceptOption, QuestionSourceApi } from '@shared-ui/question-edit-form';
import { QuestionsPage, QuestionFeedbackControl } from '@shared-ui/questions-page';
import type { QuestionDetail } from '@shared-ui/questions-page';
import type { ModuleItem } from '@shared-ui/reference-data';
import { ItemTagActions } from '@shared-ui/user-tags';
import { QuestionPicker } from '@shared-ui/question-picker';
import { Modal } from '@shared-ui/components';
import { useActionTiming } from '@shared-ui/perf';
import { useQuestionImportExport } from './useQuestionImportExport';
import { QuestionComments } from './QuestionComments';
import { QuestionCurricula, QuestionCurriculaPreview } from './QuestionCurricula';
import { QuestionQualityBadge } from './QuestionQualityBadge';
import { ImportQuestionsDialog } from './ImportQuestionsDialog';
import { BulkEditModal } from '@shared-ui/bulk-edit';
import { ALL_TENANTS, useSelectedTenantId } from '../../components/TenantFilter';
import './Questions.css';
import { QUESTION_SUBJECT_OPTIONS } from '@padcanvas/shared';

/** Admins edit every subject with a question bank — the core four plus Geography. */
const ADMIN_QUESTION_SUBJECTS: string[] = [...QUESTION_SUBJECT_OPTIONS];

// Stable reference: the form loads the source paper whenever this changes.
// See docs/FeatureDesigns/question-image-recrop-target.md.
const QUESTION_SOURCE_API: QuestionSourceApi = {
  getSource: id => questionApi.getQuestionSource(id),
  cropFromSource: (id, body) => questionApi.cropQuestionImageFromSource(id, body),
};

// Stable reference so QuestionEditForm's concept-load effect doesn't refetch
// on every parent render. Maps the concept registry → tagging-picker options.
const loadConceptsForSubject = (subject: string): Promise<ConceptOption[]> =>
  adminApi.listConcepts({ subject }).then(r =>
    r.concepts.map(c => ({ id: c.id, name: c.name, aliases: c.aliases })),
  );

// Stable reference for the book-source picker (textbook origin).
const loadBooks = () =>
  adminApi.listBooks({ activeOnly: true }).then(r =>
    r.books.map(b => ({ id: b.id, title: b.title, author: b.author, publisher: b.publisher })),
  );

export function Questions() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Save timing → Page Perf → Actions. The server half of a save is ~150ms; this
  // captures the rest (list + detail refetch, then the re-render).
  const timeAction = useActionTiming();

  // Related questions open in a NEW tab: the console's Questions page keeps
  // filters, selection and any half-finished edit in this one.
  const questionHref = useCallback((id: string) => `/questions?id=${encodeURIComponent(id)}`, []);

  // Version-history API — shared by the read-only History subsection (view) and
  // the edit form's history panel. adminApi is a stable module import.
  const historyApi = useMemo(() => ({
    list: (qid: string) => adminApi.getRevisions(qid),
    get: (qid: string, seq: number) => adminApi.getRevision(qid, seq),
    restore: (qid: string, seq: number, note?: string) => adminApi.restoreRevision(qid, seq, note),
  }), []);
  const initialQuestionId = useMemo(() => searchParams.get('id') || undefined, [searchParams]);
  // Arriving from a test's question preview (`?setId=`): list that set's
  // questions with `?id=` selected, rather than narrowing to the one row.
  const setId = searchParams.get('setId') || undefined;
  const { data: scopedSet } = useQuery({
    queryKey: ['question-set-scope', setId],
    queryFn: () => questionSetApi.get(setId!),
    enabled: !!setId,
  });
  const scopeQuestionIds = scopedSet?.questionSet?.questionIds;
  // Deep-link from an operation detail (e.g. question generation): ?batch=<importBatchId>
  // filters the list to that run; &source=teacher opens the teacher-owned view
  // (generated questions carry a teacherId, so they live under the teacher scope).
  const [teacherView, setSourceTeacher] = useState(() => searchParams.get('source') === 'teacher');

  // Sidebar TenantFilter — `ALL_TENANTS` means don't scope. When scoped, the
  // backend applies the Bucket B OR-rule (tenantId IN ('0', T) plus legacy).
  // See docs/FeatureDesigns/multi-tenancy/multi-tenancy-rollout-plan.md §T2.
  const selectedTenantId = useSelectedTenantId();
  const tenantScope = selectedTenantId === ALL_TENANTS ? undefined : selectedTenantId;

  // Fetch teachers for "Tag for teacher" in bulk edit, the Teachers-tab filter,
  // and name resolution. Scoped to the selected tenant like the question list
  // itself — omitting tenantScope here returned every tenant's teachers (and,
  // via `accessCategory=teacher-allstudents` + `teacherId`, their questions)
  // regardless of which tenant a tenant-scoped admin was viewing.
  const { data: teachersData } = useQuery({
    queryKey: ['teachers-list', tenantScope],
    queryFn: () => adminApi.getTeachers(tenantScope),
    staleTime: 5 * 60 * 1000,
  });
  const teacherOptions = useMemo(() =>
    (teachersData || []).map((t: { userId: string; name: string }) => ({ id: t.userId, name: t.name })),
    [teachersData],
  );
  const teacherNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of teacherOptions) map.set(t.id, t.name);
    return map;
  }, [teacherOptions]);
  const resolveTeacherName = useCallback((teacherId: string) => teacherNameMap.get(teacherId), [teacherNameMap]);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [dialogImportBatchId, setDialogImportBatchId] = useState<string | undefined>(() => searchParams.get('batch') || undefined);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');

  const api = {
    getQuestionList: (filters: Record<string, string | number>) => {
      const f = { ...filters } as Parameters<typeof adminApi.getQuestionList>[0] & { accessCategory?: string; teacherId?: string; tenantId?: string };
      if (teacherView) {
        f.accessCategory = 'teacher-allstudents';
        if (selectedTeacherId) f.teacherId = selectedTeacherId;
      }
      if (tenantScope) f.tenantId = tenantScope;
      return adminApi.getQuestionList(f);
    },
    getQuestionDetail: (id: string) => adminApi.getQuestionById(id) as Promise<QuestionDetail>,
  };
  const pendingFormDataRef = useRef<QuestionFormData | null>(null);

  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const selectedQuestionIdRef = useRef<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const {
    status: importExportStatus,
    clearStatus: clearImportExportStatus,
    popup: importPopup,
    clearPopup: clearImportPopup,
    isExporting,
    exportStep,
    handleExport,
    showExportDialog,
    onExportChoice,
    onExportCancel,
    setFilters,
  } = useQuestionImportExport();

  // ── Mutations ──────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (question: Question) => adminApi.saveQuestion(question),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question-list'] });
      queryClient.invalidateQueries({ queryKey: ['question-detail'] });
      setSaveStatus({ type: 'success', message: 'Question saved successfully' });
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error) => {
      alert(`Save failed: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.bulkDeleteQuestions(ids),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['question-list'] });
      const parts = [];
      if (result.deleted) parts.push(`deleted ${result.deleted}`);
      if (result.archived) parts.push(`archived ${result.archived} (referenced/attempted — kept for history)`);
      setSaveStatus({ type: 'success', message: parts.length ? parts.join(', ') : 'No questions deleted' });
      setTimeout(() => setSaveStatus(null), 4000);
    },
    onError: (error: Error) => {
      alert(`Delete failed: ${error.message}`);
    },
  });

  const publishMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.bulkPublishQuestions(ids),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['question-list'] });
      setSaveStatus({ type: 'success', message: `Published ${result.published} questions` });
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error) => {
      alert(`Publish failed: ${error.message}`);
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.bulkUnpublishQuestions(ids),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['question-list'] });
      setSaveStatus({ type: 'success', message: `Moved ${result.unpublished} question(s) to draft` });
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error) => {
      alert(`Unpublish failed: ${error.message}`);
    },
  });

  // ── Render props for QuestionsPage ────────────────────────────

  const renderHeaderActions = useCallback(() => (
    <>
      <div
        style={{
          display: 'inline-flex', borderRadius: '4px', overflow: 'hidden', fontSize: 12,
          border: '1px solid var(--border-color, #ddd)',
        }}
      >
        <button
          onClick={() => setSourceTeacher(false)}
          style={{
            padding: '4px 10px', border: 'none', cursor: 'pointer',
            background: !teacherView ? 'var(--color-primary, #3b82f6)' : 'transparent',
            color: !teacherView ? '#fff' : 'var(--text-secondary)',
          }}
        >
          Public
        </button>
        <button
          onClick={() => setSourceTeacher(true)}
          style={{
            padding: '4px 10px', border: 'none', cursor: 'pointer',
            borderLeft: '1px solid var(--border-color, #ddd)',
            background: teacherView ? 'var(--color-primary, #3b82f6)' : 'transparent',
            color: teacherView ? '#fff' : 'var(--text-secondary)',
          }}
        >
          Teachers
        </button>
      </div>
      <button className="qp-icon-btn" onClick={() => setIsCreating(true)} title="New Question" style={{ color: '#22c55e' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
      <button className="qp-icon-btn" onClick={handleExport} disabled={isExporting} title="Export">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17,8 12,3 7,8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </button>
      <button
        className="qp-icon-btn"
        onClick={() => setImportDialogOpen(true)}
        title="Import"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7,10 12,15 17,10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </button>
    </>
  ), [isExporting, handleExport, teacherView]);

  const renderDetailLeftActions = useCallback(() => (
    <button
      onClick={() => navigate('/questions/deletions')}
      title="View deleted questions"
      style={{
        padding: '6px 12px', border: '1px solid var(--border-color-dark)',
        borderRadius: 6, background: 'var(--bg-primary)', color: 'var(--text-secondary)',
        fontSize: 13, cursor: 'pointer',
      }}
    >
      Deleted
    </button>
  ), [navigate]);

  const renderAboveFilters = useCallback(() => (
    <>
      {exportStep && (
        <div className="import-progress-bar">
          <div className="import-progress-text">{exportStep}</div>
          <div className="import-progress-track">
            <div className="import-progress-fill" style={{ width: '100%', animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
        </div>
      )}
      {/* Status is shown via QuestionsPage statusMessage prop — not duplicated here */}
      {teacherView && (
        <div style={{ padding: '4px 8px' }}>
          <select
            value={selectedTeacherId}
            onChange={e => setSelectedTeacherId(e.target.value)}
            className="filter-select"
            style={{ minWidth: 180 }}
          >
            <option value="">All Teachers</option>
            {teacherOptions.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}
    </>
  ), [exportStep, teacherView, selectedTeacherId, teacherOptions]);

  const currentUserId = authApi.getUser()?.id;
  const renderDetailActions = useCallback((question: QuestionDetail, { refresh, setEditing }: { refresh: () => void; setEditing: (v: boolean) => void }) => {
    selectedQuestionIdRef.current = question.id;
    const thumbs = (
      /* Rates the QUESTION itself. Shown even on a read-only (other-tenant)
         question: flagging something wrong with it is not editing it. */
      <QuestionFeedbackControl
        questionId={question.id}
        userFeedback={question.userFeedback}
        currentUserId={currentUserId}
        onRate={async (id, rating, comment) => {
          await questionApi.rateQuestion(id, rating, comment);
          refresh();
        }}
      />
    );
    const isTQ = question.id.startsWith('tq-');
    // `canEdit` is computed by the SAME rule the write path enforces and sent
    // with the question — the console never re-derives it, so the button and
    // the guard cannot disagree. Absent (older payloads) reads as editable, so
    // this never hides an action that would in fact succeed.
    const canEdit = question.canEdit !== false;
    if (!canEdit) {
      return (
        <>
          {thumbs}
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            Read-only — this question belongs to another tenant
          </span>
        </>
      );
    }
    return (
      <>
        <button className="btn-edit" onClick={() => {
          if (isTQ) {
            const name = question.teacherId ? (teacherNameMap.get(question.teacherId) || question.teacherId) : 'a teacher';
            setTqConfirm({
              message: `This is a private question owned by ${name}.\n\nEditing teacher questions should only be done in exceptional circumstances.`,
              onConfirm: () => setEditing(true),
            });
            return;
          }
          setEditing(true);
        }}>
          Edit
        </button>
        {question.importStatus === 'live' && (
          <button
            className="btn-unpublish"
            onClick={async () => {
              try {
                const { attemptedQuestionIds } = await adminApi.checkQuestionAttempts([question.id]);
                const attempted = attemptedQuestionIds.length > 0;
                const msg = attempted
                  ? 'This question has been attempted by students.\n\nMove it to draft?'
                  : 'Move this question to draft?';
                if (!confirm(msg)) return;
              } catch {
                if (!confirm('Move this question to draft?')) return;
              }
              saveMutation.mutate({ ...question as unknown as Question, importStatus: 'draft' });
            }}
            style={{ backgroundColor: '#f59e0b', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer' }}
          >
            Move to Draft
          </button>
        )}
        {question.importStatus === 'draft' && (
          <button
            className="btn-publish"
            onClick={() => saveMutation.mutate({ ...question as unknown as Question, importStatus: 'live' })}
            style={{ backgroundColor: '#22c55e', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer' }}
          >
            Publish
          </button>
        )}
      </>
    );
  }, [saveMutation, teacherNameMap]);

  const renderEditMode = useCallback((question: QuestionDetail, { onDone, modulesGrouped }: { onDone: () => void; modulesGrouped: Record<string, ModuleItem[]> }) => {
    const q = question as unknown as Question;
    return (
      <QuestionEditForm
        allowedSubjects={ADMIN_QUESTION_SUBJECTS}
        key={q.id}
        isAdmin
        initialData={{
          subject: q.subject,
          questionText: q.questionText,
          correctAnswer: q.correctAnswer,
          questionType: q.questionType,
          errorMargin: q.errorMargin,
          difficulty: q.difficulty || q.difficultyLevel,
          options: q.options,
          solutions: q.solutions,
          hints: q.hints,
          maxHintsForStudent: q.maxHintsForStudent,
          appearedInExams: q.appearedInExams,
          usesConcepts: q.usesConcepts,
          avoidConcepts: q.avoidConcepts,
          boardGrades: q.boardGrades ?? [],
          module: q.module ?? undefined,
          questionReadiness: q.questionReadiness,
          enrichStatus: q.enrichStatus,
          enrichmentNotes: q.enrichmentNotes,
          questionDiagramUrl: q.questionDiagramUrl,
          figures: q.figures ?? undefined,
          solutionImageUrl: q.solutionImageUrl,
          questionSource: q.questionSource,
          sourceType: q.sourceType,
          bookSources: q.bookSources,
          sourceUrl: q.sourceUrl,
          relatedQuestions: q.relatedQuestions,
          marks: q.marks,
          keywords: q.keywords,
          parts: q.parts,
          requirement: q.requirement,
          sourceRegions: q.sourceRegions ?? undefined,
        }}
        onSave={(formData: QuestionFormData) => {
          // mutateAsync (not mutate) so the timer spans the invalidation too;
          // .catch keeps the rejection handled — saveMutation.onError alerts.
          const doSave = () => void timeAction('saveQuestion', () => saveMutation.mutateAsync({
            ...q,
            ...formData,
            difficulty: formData.difficulty as Question['difficulty'],
            hints: formData.hints?.filter(h => h.trim()),
            errorMargin: formData.errorMargin ?? undefined,
            relatedQuestions: formData.relatedQuestions as Question['relatedQuestions'],
          }).then(onDone)).catch(() => { /* reported by saveMutation.onError */ });
          const ownerId = q.ownerId ?? q.teacherId;
          if (ownerId) {
            const name = teacherNameMap.get(ownerId) || ownerId;
            setTqConfirm({
              message: `You are saving changes to a private question owned by ${name}.\n\nThis should only be done in exceptional circumstances.`,
              onConfirm: doSave,
            });
            return;
          }
          doSave();
        }}
        onCancel={onDone}
        saving={saveMutation.isPending}
        modulesBySubject={modulesGrouped as Record<string, ModuleItem[]>}
        onPublish={() => saveMutation.mutate({ ...q, importStatus: 'live' }, { onSuccess: onDone })}
        showPublish={q.importStatus === 'draft'}
        onUnpublish={() => saveMutation.mutate({ ...q, importStatus: 'draft' }, { onSuccess: onDone })}
        showUnpublish={q.importStatus === 'live'}
        questionId={q.id}
        teacherId={q.ownerId ?? q.teacherId}
        sourceImportId={q.sourceImportId}
        importBatchId={q.importBatchId}
        onDirtyChange={() => {}}
        formDataRef={pendingFormDataRef}
        onFixLatex={data => adminApi.fixLatex(data)}
        onUploadImage={(base64, mimeType) => adminApi.uploadQuestionImage(base64, mimeType)}
        onGetQuestionById={id => adminApi.getQuestionById(id)}
        questionHref={questionHref}
        onLoadConcepts={loadConceptsForSubject}
        onLoadBooks={loadBooks}
        renderQuestionPicker={({ selectedIds, onSelectionChange }) => (
          <QuestionPicker selectedIds={selectedIds} onSelectionChange={onSelectionChange} />
        )}
        renderCurriculaPreview={({ moduleId, classes, targetExams }) => (
          <QuestionCurriculaPreview moduleId={moduleId} classes={classes} targetExams={targetExams} />
        )}
        renderTopCenter={() => (
          <ItemTagActions itemType="question" itemId={q.id} />
        )}
        historyApi={historyApi}
        sourceApi={QUESTION_SOURCE_API}
        onHistoryRestored={() => {
          queryClient.invalidateQueries({ queryKey: ['question-list'] });
          queryClient.invalidateQueries({ queryKey: ['question-detail'] });
          onDone();
        }}
      />
    );
  }, [saveMutation, teacherNameMap, queryClient, historyApi]);

  /** If any selected IDs are teacher questions, show a warning modal before proceeding; otherwise run action directly. */
  const guardTqAction = useCallback((selectedIds: Set<string>, action: () => void) => {
    const tqCount = [...selectedIds].filter(id => id.startsWith('tq-')).length;
    if (tqCount > 0) {
      setTqConfirm({
        message: `${tqCount} of ${selectedIds.size} selected question(s) are private teacher questions.\n\nModifying teacher questions should only be done in exceptional circumstances.`,
        onConfirm: action,
      });
    } else {
      action();
    }
  }, []);

  const renderSelectionBar = useCallback((selectedIds: Set<string>, clearSelection: () => void) => (
    <>
      <button
        className="btn-selection-action"
        onClick={() => {
          guardTqAction(selectedIds, () => {
            setBulkEditIds(new Set(selectedIds));
            bulkEditClearRef.current = clearSelection;
            setBulkEditOpen(true);
          });
        }}
        style={{ background: 'var(--color-primary, #3b82f6)' }}
      >
        Bulk Edit
      </button>
      <button
        className="btn-selection-action"
        onClick={() => {
          if (!confirm(`Publish ${selectedIds.size} question(s)?`)) return;
          guardTqAction(selectedIds, () => {
            publishMutation.mutate([...selectedIds], { onSuccess: () => clearSelection() });
          });
        }}
        disabled={publishMutation.isPending}
        style={{ background: 'var(--color-success, #10b981)' }}
      >
        {publishMutation.isPending ? 'Publishing...' : 'Publish'}
      </button>
      <button
        className="btn-selection-action"
        onClick={async () => {
          const ids = [...selectedIds];
          try {
            const { attemptedQuestionIds } = await adminApi.checkQuestionAttempts(ids);
            const count = attemptedQuestionIds.length;
            const msg = count > 0
              ? `${count} of ${ids.length} question(s) have been attempted.\n\nMove to draft?`
              : `Move ${ids.length} question(s) to draft?`;
            if (!confirm(msg)) return;
          } catch {
            if (!confirm(`Move ${ids.length} question(s) to draft?`)) return;
          }
          guardTqAction(selectedIds, () => {
            unpublishMutation.mutate(ids, { onSuccess: () => clearSelection() });
          });
        }}
        disabled={unpublishMutation.isPending}
        style={{ background: '#f59e0b' }}
      >
        {unpublishMutation.isPending ? 'Moving...' : 'Move to Draft'}
      </button>
      <button
        className="btn-selection-action"
        onClick={async () => {
          const ids = [...selectedIds];
          // Best-effort warning: questions that have been attempted (and any
          // referenced by a test/paper) are ARCHIVED instead of hard-deleted —
          // hidden from lists but kept so past attempts still render.
          let attempted = 0;
          try {
            const res = await adminApi.checkQuestionAttempts(ids);
            attempted = res.attemptedQuestionIds.length;
          } catch { /* fall back to the generic prompt */ }
          const msg = attempted > 0
            ? `Delete ${ids.length} question(s)?\n\n${attempted} have been attempted by students and will be ARCHIVED (hidden from lists but kept so past attempts still render). Unreferenced questions are permanently deleted.`
            : `Delete ${ids.length} question(s)?\n\nQuestions used in a test/paper or already attempted are archived (recoverable); the rest are permanently deleted.`;
          if (!confirm(msg)) return;
          guardTqAction(selectedIds, () => {
            deleteMutation.mutate(ids, { onSuccess: () => clearSelection() });
          });
        }}
        disabled={deleteMutation.isPending}
        style={{ background: 'var(--color-error, #ef4444)' }}
      >
        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
      </button>
    </>
  ), [publishMutation, unpublishMutation, deleteMutation, guardTqAction]);

  const createMutation = useMutation({
    mutationFn: (question: Question) => adminApi.saveQuestion(question),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['question-list'] });
      queryClient.invalidateQueries({ queryKey: ['question-detail'] });
      setIsCreating(false);
      setSaveStatus({ type: 'success', message: 'Question created successfully' });
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error) => {
      alert(`Create failed: ${error.message}`);
    },
  });

  const renderCreateMode = useCallback(({ onDone, modulesGrouped }: { onDone: () => void; modulesGrouped: Record<string, ModuleItem[]> }) => {
    const handleDone = () => { setIsCreating(false); onDone(); };
    return (
      <QuestionEditForm
        allowedSubjects={ADMIN_QUESTION_SUBJECTS}
        key="__create__"
        isAdmin
        initialData={{
          subject: 'maths',
          questionText: '',
          correctAnswer: '',
          questionType: 'MCQ' as Question['questionType'],
          difficulty: 'medium',
          options: [
            { label: 'A', text: '', isCorrect: true },
            { label: 'B', text: '', isCorrect: false },
            { label: 'C', text: '', isCorrect: false },
            { label: 'D', text: '', isCorrect: false },
          ],
        }}
        onSave={(formData: QuestionFormData) => {
          void timeAction('createQuestion', () => createMutation.mutateAsync({
            ...formData,
            importStatus: 'draft',
            difficulty: formData.difficulty as Question['difficulty'],
            hints: formData.hints?.filter(h => h.trim()),
            errorMargin: formData.errorMargin ?? undefined,
          } as Question)).catch(() => { /* reported by createMutation.onError */ });
        }}
        onCancel={handleDone}
        saving={createMutation.isPending}
        modulesBySubject={modulesGrouped}
        onDirtyChange={() => {}}
        formDataRef={pendingFormDataRef}
        onFixLatex={data => adminApi.fixLatex(data)}
        onUploadImage={(base64, mimeType) => adminApi.uploadQuestionImage(base64, mimeType)}
        onGetQuestionById={id => adminApi.getQuestionById(id)}
        questionHref={questionHref}
        onLoadConcepts={loadConceptsForSubject}
        onLoadBooks={loadBooks}
        renderQuestionPicker={({ selectedIds, onSelectionChange }) => (
          <QuestionPicker selectedIds={selectedIds} onSelectionChange={onSelectionChange} />
        )}
        renderCurriculaPreview={({ moduleId, classes, targetExams }) => (
          <QuestionCurriculaPreview moduleId={moduleId} classes={classes} targetExams={targetExams} />
        )}
      />
    );
  }, [createMutation]);

  const [showPopup, setShowPopup] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditIds, setBulkEditIds] = useState<Set<string>>(new Set());
  const bulkEditClearRef = useRef<(() => void) | null>(null);

  // Teacher-question override confirmation
  const [tqConfirm, setTqConfirm] = useState<{ message: string; onConfirm: () => void } | null>(null);

  // Build combined status: if there's an import error with details, append "See details" link
  const combinedStatus = saveStatus || (importExportStatus ? {
    ...importExportStatus,
    message: importExportStatus.message + (importPopup ? ' — See details' : ''),
  } : null);

  return (
    <>
      <QuestionsPage
        allowedSubjects={ADMIN_QUESTION_SUBJECTS}
        api={api}
        isAdmin
        initialQuestionId={initialQuestionId}
        scopeQuestionIds={scopeQuestionIds}
        relatedQuestionHref={questionHref}
        queryKeySuffix={`${setId ? `set-${setId}|` : ''}${teacherView ? `teacher-${selectedTeacherId || 'all'}` : 'global'}|tenant-${tenantScope ?? 'all'}`}
        renderHeaderActions={renderHeaderActions}
        renderAboveFilters={renderAboveFilters}
        renderDetailActions={renderDetailActions}
        renderDetailLeftActions={renderDetailLeftActions}
        renderDetailBadges={(question) => (
          <ItemTagActions itemType="question" itemId={question.id} />
        )}
        renderBeforeMoreDetails={(question) => <QuestionComments question={question} />}
        renderInMoreDetails={(question) => {
          const q2 = question as unknown as { problemTypeId?: string; subject?: string };
          return (
            <>
              <QuestionQualityBadge question={question} />
              {q2.problemTypeId && (
                <div style={{ marginBottom: 12 }}>
                  <button
                    className="btn-edit"
                    onClick={() => navigate(`/problem-types/${q2.problemTypeId}?subject=${q2.subject ?? 'physics'}`)}
                    title="Explore this question's problem type and its neighbours"
                  >
                    View in type graph →
                  </button>
                </div>
              )}
              <QuestionCurricula question={question} />
            </>
          );
        }}
        historyApi={historyApi}
        renderEditMode={renderEditMode}
        renderSelectionBar={renderSelectionBar}
        statusMessage={combinedStatus}
        onClearStatus={() => { setSaveStatus(null); clearImportExportStatus(); clearImportPopup(); }}
        onStatusClick={importPopup ? () => setShowPopup(true) : undefined}
        onFiltersChange={(filters) => {
          const f = { ...filters };
          if (teacherView) {
            f.accessCategory = 'teacher-allstudents';
            if (selectedTeacherId) f.teacherId = selectedTeacherId;
          }
          setFilters(f);
        }}
        searchOverride={dialogImportBatchId}
        resolveTeacherName={resolveTeacherName}
        isCreating={isCreating}
        renderCreateMode={renderCreateMode}
      />

      <BulkEditModal
        isOpen={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        selectedIds={bulkEditIds}
        onSuccess={() => {
          // Don't clear selection immediately — user may want to see what was changed
        }}
        onBulkEdit={(ids, updates) => adminApi.bulkEditQuestions(ids, updates)}
        teachers={teacherOptions}
        onBulkTagForUser={(userId, tag, questionIds) => adminApi.bulkTagForUser(userId, tag, questionIds)}
      />

      {/* Teacher question override warning */}
      <Modal isOpen={!!tqConfirm} onClose={() => setTqConfirm(null)} title="Admin Override" size="sm">
        <div style={{ padding: '8px 0' }}>
          <div style={{
            background: 'var(--status-warning-bg)',
            border: '1px solid var(--status-warning-border)',
            color: 'var(--status-warning-text)',
            borderRadius: 'var(--radius-md)',
            padding: 12, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: 20, lineHeight: 1 }}>&#9888;</span>
            <p style={{ margin: 0, fontSize: 'var(--font-xs)', whiteSpace: 'pre-line' }}>
              {tqConfirm?.message}
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              onClick={() => setTqConfirm(null)}
              style={{
                padding: '6px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)',
                background: 'transparent', color: 'var(--text-secondary)',
                cursor: 'pointer', fontSize: 'var(--font-xs)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => { tqConfirm?.onConfirm(); setTqConfirm(null); }}
              style={{
                padding: '6px 16px', borderRadius: 'var(--radius-md)', border: 'none',
                background: 'var(--color-error)', color: '#fff',
                cursor: 'pointer', fontSize: 'var(--font-xs)', fontWeight: 600,
              }}
            >
              Proceed Anyway
            </button>
          </div>
        </div>
      </Modal>

      <ImportQuestionsDialog
        isOpen={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        teacherOptions={teacherOptions}
        onImported={(batchId) => {
          queryClient.invalidateQueries({ queryKey: ['question-list'] });
          if (batchId) setDialogImportBatchId(batchId);
        }}
      />

      <Modal isOpen={showExportDialog} onClose={onExportCancel} title="Export Questions" size="sm">
        <div className="export-dialog-options">
          <button className="export-dialog-option" onClick={() => onExportChoice(false)}>
            Export with URLs only
            <div className="subtitle">Smaller file, faster export</div>
          </button>
          <button className="export-dialog-option" onClick={() => onExportChoice(true)}>
            Export with embedded images
            <div className="subtitle">Larger file, portable (base64)</div>
          </button>
        </div>
      </Modal>

      {showPopup && importPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowPopup(false)}>
          <div style={{ background: 'var(--bg-primary, #fff)', borderRadius: 12, padding: 24, maxWidth: 600, width: '90%', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: 'var(--color-error, #dc2626)' }}>{importPopup.title}</h3>
              <button onClick={() => setShowPopup(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-tertiary)', padding: '0 4px' }}>&times;</button>
            </div>
            <div style={{ overflow: 'auto', flex: 1 }}>
              {importPopup.details.map((detail, i) => (
                <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid var(--border-color, #eee)', fontSize: 13, color: 'var(--text-secondary)', fontFamily: detail.startsWith('Question ') ? 'monospace' : 'inherit' }}>
                  {detail}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowPopup(false)} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--color-primary, #3b82f6)', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
